import type { FeatureConfig } from '~/lib/builder-types';
import type { TechSpecResolver } from './types';

export const analyticsTechSpec: TechSpecResolver = (
  stack,
  featureConfig?: FeatureConfig,
) => {
  const isMongo = stack.database === 'mongodb';
  const isPostgres =
    stack.database === 'postgres' || stack.database === 'mysql';
  const isSQLite = stack.database === 'sqlite';

  const depth =
    (featureConfig?.options['analytics-depth'] as string | undefined) ??
    'basic';
  const hasFunnels = depth === 'advanced' || depth === 'full';
  const hasCohorts = depth === 'full';

  return {
    pages: [
      {
        id: 'analytics-dashboard',
        path: '/admin/analytics',
        label: 'Analytics dashboard',
        description:
          'Overview charts: DAU/WAU/MAU, top events, retention heatmap, and key conversion KPIs',
        authRequired: true,
      },
      ...(hasFunnels
        ? [
            {
              id: 'analytics-funnels',
              path: '/admin/analytics/funnels',
              label: 'Funnels',
              description:
                'Define multi-step conversion funnels and view drop-off at each step',
              authRequired: true,
            },
          ]
        : []),
      ...(hasCohorts
        ? [
            {
              id: 'analytics-cohorts',
              path: '/admin/analytics/cohorts',
              label: 'Cohort analysis',
              description:
                'Retention grid showing cohort behaviour by signup week/month',
              authRequired: true,
            },
          ]
        : []),
    ],
    uxFlows: [
      {
        id: 'track-event',
        title: 'Client tracks an analytics event',
        steps: [
          'User performs an action (e.g. clicks "Upgrade", completes onboarding step)',
          'Client SDK calls analytics.track("upgrade_clicked", { plan, source })',
          'SDK batches events locally — flushes every 5 s or when 50 events accumulate',
          'POST /api/analytics/events sends the batch to the server',
          isPostgres
            ? 'Server bulk-inserts rows into the events table (partitioned by month for scale)'
            : isMongo
              ? 'Server bulk-inserts documents into the events collection (TTL index auto-expires raw events after 90 days)'
              : 'Server inserts rows into the events table (SQLite WAL mode recommended for write throughput)',
          'A background job (cron or queue worker) aggregates raw events into daily_metrics every hour',
          'Dashboard queries hit daily_metrics — not the raw events table',
        ],
      },
      {
        id: 'view-dashboard',
        title: 'Admin views the analytics dashboard',
        steps: [
          'Admin navigates to /admin/analytics',
          'Page calls GET /api/analytics/metrics?metric=dau&period=30d',
          isPostgres
            ? 'Server queries the daily_metrics materialized view or aggregation table; refreshed by cron'
            : isMongo
              ? 'Server runs an aggregation pipeline over daily_stats collection'
              : 'Server queries the daily_metrics table precomputed by background job',
          'Charts render with daily data points for selected period',
          'Admin can switch between 7d / 30d / 90d / custom range',
        ],
      },
      ...(hasFunnels
        ? [
            {
              id: 'funnel-analysis',
              title: 'Admin defines and views a funnel',
              steps: [
                'Admin navigates to /admin/analytics/funnels and clicks "New funnel"',
                'Admin names the funnel and adds up to 8 ordered steps (each step is an event name)',
                'POST /api/analytics/funnels saves the funnel definition',
                'GET /api/analytics/funnels/:id/results queries conversion rates between each step',
                isPostgres
                  ? 'Server uses window functions or a CTE to compute ordered event sequences per user within a 7-day window'
                  : isMongo
                    ? 'Server runs a multi-stage aggregation pipeline grouping events by userId and ordering by createdAt'
                    : 'Server joins event rows self-referentially by user_id and event sequence',
                'Funnel chart shows step-by-step conversion % and absolute user counts',
              ],
            },
          ]
        : []),
      ...(hasCohorts
        ? [
            {
              id: 'cohort-retention',
              title: 'Admin views cohort retention',
              steps: [
                'Admin navigates to /admin/analytics/cohorts',
                'GET /api/analytics/cohorts returns a retention matrix',
                isPostgres
                  ? "Server computes cohorts with date_trunc('week', first_seen_at) and joins to subsequent active_at events"
                  : 'Server groups users by signup week/month and checks for return activity in subsequent periods',
                'Retention grid renders: rows = cohort period, columns = Week 0 / Week 1 / Week 2 …',
                'Cells show % of cohort still active; color-coded from red (low) to green (high)',
              ],
            },
          ]
        : []),
    ],
    endpoints: [
      {
        id: 'track',
        method: 'POST',
        path: '/api/analytics/events',
        description:
          'Ingest a batch of client-side analytics events — fire-and-forget, always returns 200',
        requestBody:
          '{ events: { name, properties, sessionId?, timestamp }[] }',
        response: '{ ok: true }',
      },
      {
        id: 'metrics',
        method: 'GET',
        path: '/api/analytics/metrics',
        description:
          'Return pre-aggregated time-series metrics for a given metric name and period',
        response:
          '{ metric: string, period: string, data: { date: string, value: number }[] }',
      },
      {
        id: 'events-list',
        method: 'GET',
        path: '/api/analytics/events',
        description:
          'Query raw events for a user or event type (admin only, paginated)',
        response: '{ events: Event[], total: number, nextCursor?: string }',
      },
      ...(hasFunnels
        ? [
            {
              id: 'list-funnels',
              method: 'GET' as const,
              path: '/api/analytics/funnels',
              description: 'List saved funnel definitions',
              response: '{ funnels: Funnel[] }',
            },
            {
              id: 'create-funnel',
              method: 'POST' as const,
              path: '/api/analytics/funnels',
              description: 'Create a funnel definition',
              requestBody: '{ name, steps: string[] }',
              response: '{ funnel: Funnel }',
            },
            {
              id: 'funnel-results',
              method: 'GET' as const,
              path: '/api/analytics/funnels/:id/results',
              description:
                'Compute funnel conversion rates — expensive query, cache for 1 hour',
              response: '{ steps: { name, users, conversionRate }[] }',
            },
          ]
        : []),
    ],
    dbTables: isMongo
      ? [
          {
            id: 'events',
            name: 'events',
            columns: [
              { name: '_id', type: 'ObjectId', nullable: false },
              {
                name: 'userId',
                type: 'ObjectId ref users',
                nullable: true,
                notes: 'Null for anonymous/pre-auth events',
              },
              {
                name: 'anonymousId',
                type: 'string',
                nullable: true,
                notes: 'Client-generated UUID for pre-auth tracking',
              },
              { name: 'name', type: 'string', nullable: false },
              { name: 'properties', type: 'object', nullable: false },
              {
                name: 'sessionId',
                type: 'string',
                nullable: true,
                notes: 'Groups events within a single browsing session',
              },
              { name: 'createdAt', type: 'Date', nullable: false },
            ],
            notes:
              'Add TTL index on createdAt to auto-expire raw events after 90 days. Index { userId, name, createdAt } for user-level queries. Index { name, createdAt } for aggregate queries.',
          },
          {
            id: 'daily_stats',
            name: 'daily_stats',
            columns: [
              { name: '_id', type: 'ObjectId', nullable: false },
              { name: 'date', type: 'string (YYYY-MM-DD)', nullable: false },
              { name: 'eventName', type: 'string', nullable: false },
              { name: 'count', type: 'number', nullable: false },
              { name: 'uniqueUsers', type: 'number', nullable: false },
              {
                name: 'dimensions',
                type: 'object',
                nullable: true,
                notes:
                  'Optional breakdown dimensions e.g. { plan: "pro", country: "US" }',
              },
            ],
            notes:
              'Unique index on { date, eventName, dimensions } to support upsert. Populated by a scheduled aggregation job every hour.',
          },
          ...(hasFunnels
            ? [
                {
                  id: 'funnels',
                  name: 'funnels',
                  columns: [
                    { name: '_id', type: 'ObjectId', nullable: false },
                    { name: 'name', type: 'string', nullable: false },
                    { name: 'steps', type: 'string[]', nullable: false },
                    {
                      name: 'createdBy',
                      type: 'ObjectId ref users',
                      nullable: false,
                    },
                    { name: 'createdAt', type: 'Date', nullable: false },
                  ],
                },
              ]
            : []),
        ]
      : [
          {
            id: 'events',
            name: 'events',
            columns: [
              { name: 'id', type: 'uuid', nullable: false },
              {
                name: 'user_id',
                type: 'uuid FK users',
                nullable: true,
                notes:
                  'Null for anonymous events; link to users after identity resolution',
              },
              {
                name: 'anonymous_id',
                type: 'text',
                nullable: true,
                notes:
                  'Client-generated UUID for pre-auth / anonymous tracking',
              },
              { name: 'event_type', type: 'text', nullable: false },
              {
                name: 'properties',
                type: isPostgres ? 'jsonb' : 'text (JSON)',
                nullable: false,
                notes: isPostgres
                  ? 'Use jsonb for GIN indexing on property keys'
                  : 'Store as JSON string; parse in application layer',
              },
              {
                name: 'session_id',
                type: 'text',
                nullable: true,
                notes:
                  'Groups events within one browsing session for funnel analysis',
              },
              { name: 'created_at', type: 'timestamp', nullable: false },
            ],
            notes: isPostgres
              ? 'Partition by month: CREATE TABLE events PARTITION BY RANGE (created_at). Index (user_id, event_type, created_at). Index (event_type, created_at) for aggregate queries. Consider TimescaleDB hypertable for very high volume.'
              : isSQLite
                ? 'Index (user_id, event_type, created_at). Use WAL journal mode for concurrent write performance. Consider archiving rows older than 90 days.'
                : 'Index (user_id, event_type, created_at). Index (event_type, created_at) for aggregate queries.',
          },
          {
            id: 'daily_metrics',
            name: 'daily_metrics',
            columns: [
              {
                name: 'date',
                type: 'date',
                nullable: false,
                notes: 'Composite PK part 1',
              },
              {
                name: 'metric_type',
                type: 'text',
                nullable: false,
                notes: 'Composite PK part 2 — e.g. dau, event_count, page_view',
              },
              { name: 'value', type: 'bigint', nullable: false },
              {
                name: 'dimensions',
                type: isPostgres ? 'jsonb' : 'text (JSON)',
                nullable: true,
                notes: 'Optional breakdown e.g. { plan: "pro" }',
              },
            ],
            notes: isPostgres
              ? 'Composite PK (date, metric_type, dimensions). Populated by a nightly cron job or REFRESH MATERIALIZED VIEW. Use a materialized view for complex aggregations.'
              : 'Composite PK (date, metric_type). Populated by a scheduled background job every hour.',
          },
          ...(hasFunnels
            ? [
                {
                  id: 'funnels',
                  name: 'funnels',
                  columns: [
                    { name: 'id', type: 'uuid', nullable: false },
                    { name: 'name', type: 'text', nullable: false },
                    {
                      name: 'steps',
                      type: isPostgres ? 'text[]' : 'text (JSON array)',
                      nullable: false,
                      notes: 'Ordered list of event_type values',
                    },
                    {
                      name: 'created_by',
                      type: 'uuid FK users',
                      nullable: false,
                    },
                    { name: 'created_at', type: 'timestamp', nullable: false },
                  ],
                },
              ]
            : []),
        ],
  };
};
